import { FeatureEnum } from '@/features/game/levels/config/features'
import { Plugin } from '@/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fullApiSlice } from './api'
import { FeatureAccessUpdate } from '@/types/game/featureAccess'
import { featureAccessInitialState, PLUGINS_TO_FEATURES } from './config'

const IS_NEW_USER_MODE = true
const lockedForNewUser = IS_NEW_USER_MODE ? false : true

export type State = {
    featureAccessMap: { [key in FeatureEnum]?: boolean }
    plugins: Record<Plugin, boolean>
}

const unlockFeatures = (features: FeatureEnum[], state: State) => {
    for (let feat of features) {
        if (!(feat in state.featureAccessMap)) {
            state.featureAccessMap[feat] = false // unlock features
        }
    }
}

const enableFeatures = (features: FeatureEnum[], state: State) => {
    for (let feat of features) {
        state.featureAccessMap[feat] = true // enable features
    }
}

const levelSlice = createSlice({
    name: 'feature-access',
    initialState: featureAccessInitialState,
    reducers: {
        setFeatureAccess: (state, action: PayloadAction<FeatureAccessUpdate>) => {
            const { enable, unlock, lock } = action.payload
            if (enable) {
                enableFeatures(enable, state)
            }
            if (unlock) {
                unlockFeatures(unlock, state)
            }
            if (lock) {
                for (let feat of lock) {
                    delete state.featureAccessMap[feat]
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(fullApiSlice.endpoints.getUser.matchFulfilled, (state, action) => {
            // pretend this field and this payload data exist for sake of example
            const { level, plugins } = action.payload
            if (plugins) {
                for (const p of plugins) {
                    state.plugins[p] = true
                    if (PLUGINS_TO_FEATURES[p]) {
                        unlockFeatures(PLUGINS_TO_FEATURES[p].unlocked, state)
                        enableFeatures(PLUGINS_TO_FEATURES[p].enabled, state)
                    }
                }
            }

            // TODO: This should be done on BE
            if (state.plugins['alpha']) {
                state.plugins['beta'] = true
            }
        })

        builder.addMatcher(
            fullApiSlice.endpoints.getFeatureAccess.matchFulfilled,
            (state, action) => {
                // pretend this field and this payload data exist for sake of example
                const { enabled, unlocked } = action.payload
                enableFeatures(enabled, state)
                unlockFeatures(unlocked, state)
            }
        )

        builder.addMatcher(fullApiSlice.endpoints.getMobile.matchFulfilled, (state, action) => {
            // pretend this field and this payload data exist for sake of example
            const { isPaired } = action.payload
            state.plugins['mobileApp'] = isPaired
        })
    },
})

export const { setFeatureAccess } = levelSlice.actions
export default levelSlice.reducer
